import {
  Modal,
  BlockStack,
  Text,
  TextField,
  InlineGrid,
  Button,
  Link,
  Banner,
  Select,
} from "@shopify/polaris";
import { useEffect, useState } from "react";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { numberWithCommas, formatPlate } from "../../utility/funcs";
import ZoomableImage from "../ZoomableImage";

// How much closer another car's km must be to the photo before warning.
const MISMATCH_KM = 500;

function OdometerReviewModal({ submission, open, onClose, onUpdated }) {
  const [notedOdometer, setNotedOdometer] = useState("");
  const [reason, setReason] = useState("");
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  // Whether the reviewer is being asked to confirm a value different from
  // what the AI read from the photo.
  const [confirming, setConfirming] = useState(false);
  // Check-in / checkout this request came from, with the km typed there.
  const [inspection, setInspection] = useState(null);
  // The reservation had a car change: which of its cars the photo shows.
  const [carId, setCarId] = useState(null);
  const [approveError, setApproveError] = useState(null);
  const { session } = UserAuth();

  useEffect(() => {
    setConfirming(false);
    setApproveError(null);
    setInspection(null);
    setCarId(submission?.car_id ?? null);
    if (!submission?.inspection_id) return;
    supabase
      .from("reservation_inspections")
      .select("type, odometer, reservation_id")
      .eq("id", submission.inspection_id)
      .maybeSingle()
      .then(({ data }) => setInspection(data || null));
  }, [submission?.id, submission?.inspection_id, submission?.car_id]);

  if (!submission) return null;

  const detected = submission.ai_odometer ?? null;
  const candidates = submission.car_candidates || [];
  const selected = candidates.find((c) => c.car_id === carId);
  // After a car change the car comes from the change dates, which can be off
  // by a day; warn only when the dash clearly matches another car better.
  const gap = (c) => Math.abs(detected - c.system);
  const assigned = candidates.find((c) => c.car_id === submission.car_id);
  const closest =
    detected != null && candidates.length
      ? candidates.reduce((a, b) => (gap(b) < gap(a) ? b : a))
      : null;
  const otherCarMatches =
    assigned && closest && closest.car_id !== assigned.car_id &&
    gap(assigned) - gap(closest) > MISMATCH_KM;
  const systemOdometer = selected
    ? selected.system
    : submission.system_odometer_at_submission;

  const differsFromDetected =
    detected != null && Number(notedOdometer) !== Number(detected);

  const handleClose = () => {
    setConfirming(false);
    onClose();
  };

  // -----------------------------
  // HANDLE APPROVE
  // -----------------------------
  const handleApprove = async () => {
    if (!notedOdometer || Number(notedOdometer) <= 0) {
      console.error("Odometer invalid.");
      return;
    }

    setApproveLoading(true);
    setApproveError(null);

    try {
      // Another car picked: the request takes that car's km at the photo time.
      if (selected && selected.car_id !== submission.car_id) {
        const { error } = await supabase
          .from("car_odometer_submissions")
          .update({
            car_id: selected.car_id,
            system_odometer_at_submission: selected.system,
          })
          .eq("id", submission.id);
        if (error) {
          setApproveError(error.message);
          return;
        }
      }

      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/approve_odometer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            submission_id: submission.id,
            noted_odometer: Number(notedOdometer),
          }),
        }
      );

      const data = await res.json();

      // Nexofleet can refuse the reading (e.g. no GPS data before the photo).
      if (!res.ok || !data.success) {
        setApproveError(data?.error || "No se pudo aprobar");
        setConfirming(false);
        return;
      }

      // SUCCESS: Remove row and close modal
      onUpdated(submission.id);
      setReason("");
      setNotedOdometer("");
      handleClose();
    } catch (error) {
      setApproveError(error.message);
    } finally {
      setApproveLoading(false);
    }
  };

  // -----------------------------
  // HANDLE REJECT
  // -----------------------------
  const handleReject = async () => {
    setRejectLoading(true);

    try {
      const { error } = await supabase
        .from("car_odometer_submissions")
        .update({
          status: "rejected",
          admin_note: reason,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", submission.id);

      if (error) {
        console.error("Reject error:", error);
        return;
      }

      onUpdated(submission.id);
      setReason("");
      setNotedOdometer("");
      handleClose();
    } catch (err) {
      console.error("Connection error:", err);
    } finally {
      setRejectLoading(false);
    }
  };

  if (confirming)
    return (
      <Modal
        open={open}
        onClose={handleClose}
        title="¿Estás seguro?"
        primaryAction={{
          content: `Sí, aprobar ${numberWithCommas(notedOdometer)} km`,
          destructive: true,
          loading: approveLoading,
          onAction: handleApprove,
        }}
        secondaryActions={[{ content: "Volver", onAction: () => setConfirming(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Banner tone="critical">
              <Text>
                Capturaste <b>{numberWithCommas(notedOdometer)} km</b>, pero en la
                foto se detectó <b>{numberWithCommas(detected)} km</b>. El ajuste
                del odómetro se hará con el valor que capturaste.
              </Text>
            </Banner>
            <ZoomableImage src={submission.photo_url} alt="Odómetro" />
          </BlockStack>
        </Modal.Section>
      </Modal>
    );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Revisión - ${formatPlate(submission.car_id.plate)}`}
      size="large"
    >
      <Modal.Section>
        <InlineGrid columns={2} gap={400}>
          <ZoomableImage src={submission.photo_url} alt="Odómetro" />

          <BlockStack gap="300">
            <Text variant="headingMd">Información Capturada</Text>

            {approveError && <Banner tone="critical">{approveError}</Banner>}

            {(otherCarMatches || carId !== submission.car_id) && (
              <Banner tone="warning">
                <BlockStack gap="200">
                  <Text>
                    Esta reserva tuvo cambio de unidad y el odómetro de la foto
                    coincide más con {formatPlate(closest?.plate)}. Confirma de qué auto es la
                    foto antes de aprobar.
                  </Text>
                  <Select
                    label="Auto"
                    value={String(carId)}
                    onChange={(v) => setCarId(Number(v))}
                    options={candidates.map((c) => ({
                      value: String(c.car_id),
                      label:
                        `${formatPlate(c.plate)} — sistema ${numberWithCommas(c.system)} km` +
                        (detected != null
                          ? ` (dif. ${numberWithCommas(detected - c.system)} km)`
                          : ""),
                    }))}
                  />
                </BlockStack>
              </Banner>
            )}

            {inspection && (
              <Text>
                <b>Origen:</b>{" "}
                <Link url={`/reservation?id=${inspection.reservation_id}`} target="_blank">
                  {inspection.type === "checkout" ? "Entrega" : "Devolución"} · Reserva #
                  {inspection.reservation_id}
                </Link>
                {inspection.odometer
                  ? ` (capturado: ${numberWithCommas(inspection.odometer)} km)`
                  : ""}
              </Text>
            )}

            {submission.service_id && (
              <Text>
                <b>Origen:</b>{" "}
                <Link url={`/services/${submission.service_id}`} target="_blank">
                  Servicio #{submission.service_id}
                </Link>
              </Text>
            )}

            <Text>
              <b>Odómetro (Sistema):</b>{" "}
              {numberWithCommas(systemOdometer)} km
            </Text>

            {detected != null && (
              <Text>
                <b>Detectado en la foto:</b> {numberWithCommas(detected)} km
              </Text>
            )}

            {notedOdometer ? (
              <Text tone="critical" fontWeight="bold">
                Diferencia:{" "}
                {numberWithCommas(
                  Number(notedOdometer) - Number(systemOdometer)
                )}
                km
              </Text>
            ) : (
              <Text>‎</Text>
            )}

            <TextField
              label="Odometría observada en la foto"
              type="number"
              value={notedOdometer}
              onChange={setNotedOdometer}
              autoComplete="off"
            />

            <TextField
              label="Razón de rechazo (opcional)"
              multiline={4}
              value={reason}
              onChange={setReason}
            />

            <InlineGrid columns={2} gap="400">
              <Button
                tone="critical"
                loading={rejectLoading}
                onClick={handleReject}
              >
                Rechazar
              </Button>

              <Button
                variant="primary"
                loading={approveLoading}
                onClick={differsFromDetected ? () => setConfirming(true) : handleApprove}
                disabled={!notedOdometer}
              >
                Aprobar
              </Button>
            </InlineGrid>
          </BlockStack>
        </InlineGrid>
      </Modal.Section>
    </Modal>
  );
}

export default OdometerReviewModal;
